import { useContext, useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import { Container } from "@mui/material";

import {
  ActionIcon,
  Button,
  CopyButton,
  Divider,
  Modal,
  Skeleton,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconBrandWhatsapp,
  IconCopy,
  IconExternalLink,
  IconRestore,
} from "@tabler/icons-react";

import dayjs from "dayjs";

import {
  BrandImages,
  Colors,
  HttpSuccessCodes,
  ValidIDLength,
} from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  convertPhoneToWhatsapp,
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
} from "../../Lib/Methods";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  PlanProps,
  PossibleServices,
  TransactionProps,
  UserProfileProps,
  UserSubscriptionProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";

import { AppContext } from "../Dashboard";
import "./styles.scss";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";

const SingleSubscriptionPage: React.FC = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const { subscriptionID } = useParams();
  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    reloadData: reloadSubscription,
  } = usePerformRequest<NonPaginatedResponse<UserSubscriptionProps>>({
    method: "GET",
    route: Endpoints.GetSingleSubscription(subscriptionID as string),
  });
  const {
    data: plan,
    isLoading: isLoadingPlan,
    reloadData: reloadPlan,
  } = usePerformRequest<NonPaginatedResponse<PlanProps>>({
    method: "GET",
    route: Endpoints.GetSinglePlan(subscription?.data.planID as string),
  });
  const {
    data: transaction,
    isLoading: isLoadingTransaction,
    reloadData: reloadTransaction,
  } = usePerformRequest<NonPaginatedResponse<TransactionProps>>({
    method: "GET",
    route: Endpoints.GetSingleTransaction(
      subscription?.data.transactionID as string
    ),
  });
  const {
    data: user,
    isLoading: isLoadingUser,
    reloadData: reloadUser,
  } = usePerformRequest<NonPaginatedResponse<UserProfileProps>>({
    method: "GET",
    route: Endpoints.GetUserProfile(subscription?.data.userID as string),
  });

  useEffect(() => {
    if (subscription && subscription.data.id) {
      reloadPlan();
      reloadTransaction();
    }
  }, [subscription]);

  const isSubscriptionExpired = () => {
    if (subscription) {
      return dayjs().isAfter(dayjs(subscription?.data?.expiresAt));
    } else {
      return true;
    }
  };
  const isPlanExpired = () => {
    if (plan) {
      return dayjs().isAfter(dayjs(plan?.data?.expirationDate));
    } else {
      return true;
    }
  };

  const [
    isReassignModalOpen,
    { open: OpenReassignModal, close: CloseReassignModal },
  ] = useDisclosure(false);

  const [isReassigning, setReassigning] = useState<boolean>(false);
  const reassignForm = useForm({
    initialValues: {
      planID: "",
    },
    validate: {
      planID: (value) =>
        value && value.length === ValidIDLength
          ? null
          : "Please enter valid ID",
    },
  });

  const ReassignSubscription = async () => {
    const { hasErrors: formHasErrors } = reassignForm.validate();
    if (!formHasErrors) {
      setReassigning(true);
      const { planID } = reassignForm.values;
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetReassignUserToPlan(),
        data: {
          newPlanID: planID,
          oldPlanID: plan?.data?.id,
          subscriptionID: subscription?.data?.id,
          userID: subscription?.data?.userID,
        },
        callback: () => {
          setReassigning(false);
        },
      });
      setReassigning(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification("Subscription Reassigned");
          CloseReassignModal();
          reloadSubscription().then(() => {
            reloadPlan();
            reloadTransaction();
          });
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };
  const [isRemovingUserFromPlan, setRemovingUserFromPlan] = useState(false);

  const RemoveUserFromPlan = async (subscriptionID: string) => {
    setRemovingUserFromPlan(true);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetRemoveUserFromPlan(subscriptionID),
      callback: () => {
        setRemovingUserFromPlan(false);
      },
    });
    setRemovingUserFromPlan(false);
    if (r.status === 200) {
      reloadSubscription();
      DefaultSuccessNotification("Subscription expired successfully");
    } else {
      DefaultErrorNotification(r.message);
    }
  };
  return (
    <Container maxWidth="md">
      <div className="single-subscription-container">
        <div className="flex-row align-center justify-between width-100">
          <div className="flex-col">
            <Text fw={500} fz={17} c={Colors.GreenSecondary}>
              REFERENCE
            </Text>
            {isLoadingPlan || isLoadingSubscription ? (
              <Skeleton height={30} radius="xs" w="120px" />
            ) : (
              <CopyButton value={transaction?.data?.reference ?? ""}>
                {({ copied, copy }) => (
                  <div className="flex-row align-center">
                    <Text c={copied ? Colors.GreenPrimary : Colors.BluePrimary}>
                      {transaction?.data?.reference}
                    </Text>
                    <ActionIcon
                      ml={5}
                      size="sm"
                      variant="subtle"
                      onClick={copy}
                    >
                      <IconCopy size={16} />
                    </ActionIcon>
                  </div>
                )}
              </CopyButton>
            )}
          </div>

          <Link to={`/d/support/new?subscription=${subscriptionID}`}>
            <Button>Dispute</Button>
          </Link>
        </div>
        <img
          src={BrandImages.retrieveImage(
            subscription?.data?.service as PossibleServices
          )}
          className="service-img"
          alt=""
        />
        <Table
          mt={30}
          verticalSpacing="md"
          variant="vertical"
          layout="fixed"
          withRowBorders
          withTableBorder
        >
          <Table.Thead>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th w={220} fw={500}>
                Service
              </Table.Th>
              <Table.Td>
                {isLoadingSubscription ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text fz="sm">{subscription?.data?.service}</Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>Initial Subscription Date</Table.Th>
              <Table.Td>
                {isLoadingSubscription ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text fz="sm" c={Colors.BluePrimary}>
                      {dayjs(subscription?.data?.createdAt).format(
                        "ddd, DD MMM YYYY"
                      )}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>Last Subscription Date</Table.Th>
              <Table.Td>
                {isLoadingSubscription ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text fz="sm" c={Colors.BluePrimary}>
                      {dayjs(subscription?.data?.lastSubscriptionDate).format(
                        "ddd, DD MMM YYYY"
                      )}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>Expires On</Table.Th>
              <Table.Td>
                {isLoadingSubscription ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text
                      fz="sm"
                      c={
                        isSubscriptionExpired()
                          ? Colors.RedPrimary
                          : Colors.GreenPrimary
                      }
                    >
                      {dayjs(subscription?.data?.expiresAt).format(
                        "ddd, DD MMM YYYY"
                      )}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>User's ID</Table.Th>
              <Table.Td>
                {isLoadingUser ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <CopyButton value={user?.data?.id ?? ""}>
                      {({ copied, copy }) => (
                        <Text
                          fz={14}
                          className="pointer"
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                          onClick={copy}
                        >
                          {user?.data?.id}
                        </Text>
                      )}
                    </CopyButton>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>User's Name</Table.Th>
              <Table.Td>
                {isLoadingUser ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text fz={14}>{user?.data?.name}</Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>User's Email</Table.Th>
              <Table.Td>
                {isLoadingUser ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <CopyButton value={user?.data?.email ?? ""}>
                      {({ copied, copy }) => (
                        <Text
                          fz={14}
                          className="pointer"
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                          onClick={copy}
                        >
                          {user?.data?.email}
                        </Text>
                      )}
                    </CopyButton>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>User's Phone Number</Table.Th>
              <Table.Td>
                {isLoadingUser ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <CopyButton value={user?.data?.phone ?? ""}>
                      {({ copied, copy }) => (
                        <Text
                          fz={14}
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                        >
                          {user?.data?.phone}
                        </Text>
                      )}
                    </CopyButton>
                    <ActionIcon
                      variant="subtle"
                      size="md"
                      ml={5}
                      color={Colors.GreenPrimary}
                      onClick={() => {
                        window.open(
                          `https://wa.me/${convertPhoneToWhatsapp(
                            user?.data?.phone as string
                          )}`,
                          "_blank"
                        );
                      }}
                    >
                      <IconBrandWhatsapp size={18} />
                    </ActionIcon>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>

            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>Plan ID</Table.Th>
              <Table.Td>
                {isLoadingPlan ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <CopyButton value={plan?.data?.id ?? ""}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied!" : "Copy to clipboard"}
                        >
                          <Text
                            c={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            className="pointer"
                            onClick={copy}
                          >
                            {plan?.data.id}
                          </Text>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th fw={500}>Plan Expires On</Table.Th>
              <Table.Td>
                {isLoadingPlan ? (
                  <Skeleton height={25} radius="xs" w="100%" />
                ) : (
                  <div className="flex-row align-center">
                    <Text
                      fz="sm"
                      c={
                        isPlanExpired()
                          ? Colors.RedPrimary
                          : Colors.GreenPrimary
                      }
                    >
                      {dayjs(plan?.data?.expirationDate).format(
                        "ddd, DD MMM YYYY"
                      )}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            {plan && plan?.data?.service === "Spotify Premium" && (
              <Table.Tr key={generateRandomString(12)}>
                <Table.Th fw={400}>Invite Link</Table.Th>
                <Table.Td>
                  {isLoadingSubscription ? (
                    <Skeleton height={25} radius="xs" w="100%" />
                  ) : (
                    <div className="flex-row align-center">
                      <CopyButton
                        value={subscription?.data?.spotifyLink as string}
                      >
                        {({ copied, copy }) => (
                          <Button
                            radius="40px"
                            size="xs"
                            onClick={copy}
                            color={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            leftSection={<IconCopy size={15} />}
                          >
                            Copy
                          </Button>
                        )}
                      </CopyButton>
                      <Button
                        radius="40px"
                        size="xs"
                        onClick={() => {
                          window.open(subscription?.data?.spotifyLink);
                        }}
                        ml={10}
                        color={Colors.GreenPrimary}
                        leftSection={<IconExternalLink size={15} />}
                      >
                        Open
                      </Button>
                    </div>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {plan && plan?.data?.service === "Duolingo Super" && (
              <Table.Tr key={generateRandomString(12)}>
                <Table.Th fw={400}>Invite Link</Table.Th>
                <Table.Td>
                  {isLoadingSubscription ? (
                    <Skeleton height={25} radius="xs" w="100%" />
                  ) : (
                    <div className="flex-row align-center">
                      <CopyButton
                        value={subscription?.data?.duolingoLink as string}
                      >
                        {({ copied, copy }) => (
                          <Button
                            radius="40px"
                            size="xs"
                            onClick={copy}
                            color={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            leftSection={<IconCopy size={15} />}
                          >
                            Copy
                          </Button>
                        )}
                      </CopyButton>
                      <Button
                        radius="40px"
                        size="xs"
                        onClick={() => {
                          window.open(subscription?.data?.duolingoLink);
                        }}
                        ml={10}
                        color={Colors.GreenPrimary}
                        leftSection={<IconExternalLink size={15} />}
                      >
                        Open
                      </Button>
                    </div>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {plan &&
              plan?.data?.service === "Spotify Premium" &&
              subscription?.data?.canRequestNewSpotifyLink && (
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th fw={400}>Reset Invite</Table.Th>
                  <Table.Td>
                    {isLoadingSubscription ? (
                      <Skeleton height={25} radius="xs" w="100%" />
                    ) : (
                      <div className="flex-col">
                        <Text fz={14}>
                          If you need a new invite link to join the Spotify
                          Plan, you can use the button below to request.
                        </Text>

                        <Button
                          radius="40px"
                          variant="outline"
                          size="sm"
                          w={120}
                          onClick={() => {
                            window.open(subscription?.data?.spotifyLink);
                          }}
                          mt={10}
                          color={Colors.GreenPrimary}
                          leftSection={<IconRestore size={15} />}
                        >
                          New
                        </Button>
                      </div>
                    )}
                  </Table.Td>
                </Table.Tr>
              )}
          </Table.Thead>
        </Table>
        {isLoadingSubscription ? (
          <Skeleton height={80} mt={30} radius="xs" w="100%" />
        ) : (
          <>
            {subscription?.data?.service === "Spotify Premium" &&
              plan?.data?.service === "Spotify Premium" && (
                <div className="flex-col">
                  <Text fw={500} mt={30} fz={17} c={Colors.GreenSecondary}>
                    ADDRESS INFORMATION
                  </Text>
                  <Text>{plan?.data?.address.value}</Text>
                  <div className="flex-row width-100">
                    <img
                      src={plan?.data?.address.image}
                      alt=""
                      className="address-image"
                    />
                  </div>
                </div>
              )}
          </>
        )}
        <Divider my={20} />
        <div className="flex-row align-center">
          <Button color={Colors.GreenPrimary} onClick={OpenReassignModal}>
            Reassign User
          </Button>
          <Button
            color={Colors.RedPrimary}
            onClick={() => {
              RemoveUserFromPlan(subscription?.data?.id as string);
            }}
            loading={isRemovingUserFromPlan}
          >
            Expire Subscription
          </Button>
        </div>

        <Modal
          centered
          opened={isReassignModalOpen}
          title="Change User Plan"
          onClose={() => {
            CloseReassignModal();
            reassignForm.reset();
          }}
        >
          <TextInput
            label="Plan ID"
            placeholder="Enter Plan ID"
            {...reassignForm.getInputProps("planID")}
            spellCheck={false}
          />
          <Button
            mt={10}
            color={Colors.GreenPrimary}
            onClick={ReassignSubscription}
            loading={isReassigning}
          >
            Reassign
          </Button>
        </Modal>
        <div className="flex-col"></div>
      </div>
    </Container>
  );
};

export default SingleSubscriptionPage;
